"use client";

import React from "react";
import { ArrowRight, BarChart3, BriefcaseBusiness, CircleCheck, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceThemes = ["Financial management and reporting", "First Nation advisory support", "Project delivery and funding accountability", "Systems, strategy, and capacity building"];
const serviceCards = [{
  title: "Advisory Support",
  description: "Practical guidance for governance, management, reporting, and decision-making.",
  icon: Handshake
}, {
  title: "Financial Clarity",
  description: "Support for controllership, year-end work, audit readiness, and financial recovery.",
  icon: BarChart3
}, {
  title: "Project Follow-Through",
  description: "Planning, coordination, reporting, and systems that help important work move forward.",
  icon: BriefcaseBusiness
}];
export default function OurFinancialExpertise() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="145123" id="our-professional-capabilities" className="bg-gradient-to-br from-background via-primary-50/35 to-secondary-50/40 text-foreground relative overflow-hidden py-16 md:py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.10),transparent_38%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(17,47,75,0.07),transparent_42%)] pointer-events-none" />

      <div ref={contentRef} className={`max-w-7xl mx-auto px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center rounded-md border border-secondary/25 bg-secondary/10 px-4 py-2 text-sm font-default font-bold text-secondary mb-6">
              <span className="flex h-2 w-2 rounded-full bg-secondary mr-2" />
              Financial Management • Advisory • Capacity Building
            </div>

            <h1 className="max-w-4xl text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight leading-tight mb-6 text-primary-500">
              The RiverBend Approach
            </h1>

            <p className="max-w-2xl text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed font-default">
              We help organizations strengthen financial systems, improve
              reporting, manage obligations, and make better decisions with
              practical, relationship-based advisory support.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 max-w-2xl">
              {serviceThemes.map((theme, index) => <div key={theme} data-index={index} className="flex items-start gap-3">
                  <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                  <span className="text-primary-700 font-default font-medium">
                    {theme}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 rounded-md bg-secondary hover:bg-secondary/90 text-white font-default">
                <a href="#services" className="inline-flex items-center justify-center">
                  Explore Our Services
                  <ArrowRight className="ml-2 size-4" />
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 rounded-md border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-default">
                <a href="/contact/" className="inline-flex items-center justify-center">
                  Talk to RiverBend
                  <ArrowRight className="ml-2 size-4" />
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-1 gap-5">
              {serviceCards.map((item, index) => {
              const Icon = item.icon;
              return <Card key={item.title} data-index={index} className="border-border bg-background/95 shadow-sm rounded-lg">
                    <CardContent className="p-6 flex gap-5 items-start">
                      <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                        <Icon className="size-6 text-secondary" />
                      </div>

                      <div>
                        <h3 className="text-xl font-default font-bold text-primary-500 mb-2">
                          {item.title}
                        </h3>

                        <p className="text-muted-foreground font-default leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}